import { z } from 'zod';
import { DisplayName, PackageName, Ulid, UserIdentity } from './common.js';
/**
 * Playgrounds (docs/playground-plan.md) — a browser workspace attached to one `web` app: an
 * AI chat, a file tree and a live preview, backed by a per-session E2B sandbox.
 *
 * The workspace is compute, not state. The project's files live in one R2 object the Worker
 * brokers but never opens, and everything the playground publishes re-enters the API through
 * the ordinary three-call upload protocol (plan §6) — so a build made here is validated by
 * exactly the checks a build uploaded from Unity or CI is.
 */
/**
 * Which kind of project an empty playground is seeded with. `starter` is the original, on either
 * engine. `boardjs` is the pixi engine's second starter, the same game written on BoardJS
 * (`docs/boardjs-plan.md` W3): pixi only, and a new one only behind `playground.boardjs`. Which
 * harness a project gets afterwards follows its own `package.json`, never this column.
 */
export const PlaygroundTemplate = z.enum(['starter', 'boardjs']);
/**
 * How the game draws (docs/playground-engine-plan.md).
 *
 * `canvas` is the original starter — hand-rolled Canvas 2D, no dependencies. `pixi` is PixiJS
 * v8, whose retained scene graph is what makes an inspector possible: an object a person can
 * click on in the preview and drag has to be an object that still exists between frames.
 *
 * Chosen when the playground is created and never changed afterwards. The engine also decides
 * which harness the assistant works under and which starter seeds an empty project, and both of
 * those are derived from it rather than stored a second time.
 */
export const PlaygroundEngine = z.enum(['canvas', 'pixi']);
/**
 * `starting` is a claim, not a phase: the row is moved into it with a conditional UPDATE so
 * two tabs pressing Connect at once cannot both pay for a sandbox.
 */
export const PlaygroundStatus = z.enum(['stopped', 'starting', 'running']);
/**
 * How big a machine this team's sandboxes get (`teams.sandbox_size`, migration 0029).
 *
 * A **team** setting rather than a per-playground one, and set by an operator in the admin
 * console — the same hand and the same reasoning as `teams.playground_seats`, which is the
 * other half of the same sentence: seats bound *how many* sandboxes at once, this bounds *how
 * big each one is*. Nothing a team can reach writes either.
 *
 * ## A size is a template, because that is the only thing E2B lets it be
 *
 * `POST /sandboxes` takes a template id, a timeout and environment variables, and nothing else
 * — cpu and memory are fixed when the **template** is built (`cpuCount`/`memoryMB` on
 * `Template.build`, read off E2B's own SDK types rather than guessed). So a deployment that
 * wants more than one size publishes the image more than once, and the API picks the template
 * id for the size (`E2B_TEMPLATE_ID`, `…_SMALLPLUS`, `…_LARGE`, and so on).
 *
 * The consequence is stated rather than smoothed over: a size whose template a deployment has
 * not published cannot be served, and the session start says exactly which var is missing
 * instead of quietly handing out a medium. A team set to `large` gets a large sandbox or an
 * error — never a smaller machine wearing the word.
 */
export const SandboxSize = z.enum(['small', 'smallplus', 'medium', 'mediumplus', 'mediummax', 'large', 'extralarge']);
/**
 * What a team's ChatGPT sandboxes are with nothing set, and what every team was moved to
 * (owner, 2026-10-27, migration 0093).
 *
 * It was `medium`, E2B's own default of 2 vCPU and 1 GiB, until production said otherwise: 13 of
 * 29 sandboxes sat at the 1 GiB ceiling and E2B's logs show the OOM killer taking headless Chrome,
 * Codex and agentd, while the second core was busy for under a tenth of a session even at the
 * 90th percentile. One core and twice the memory is the machine that work is, and costs about 70%
 * of what `medium` did — a core costs E2B about three times what a GiB does.
 */
export const DEFAULT_SANDBOX_SIZE = 'smallplus';
/**
 * The sizes, and the machine each one means.
 *
 * These numbers are the *contract with the template builder*, not something the API can enforce:
 * `playground/scripts/publish-template.mjs --size large` reads them, and a template published by
 * hand with different ones would silently make the word mean something else. That is the same
 * kind of hand-written agreement `FEATURES` has with the `features` table, and it is written
 * down in one place here for the same reason.
 *
 * Ordered by the machine rather than by name — cheapest first, which is the order the console
 * lists them in. That ordering is the only thing that says `mediumplus` sits between `medium` and
 * `large`, since nothing here ranks sizes and nothing needs to: a size is a choice from a list,
 * never a threshold something is compared against.
 */
export const SANDBOX_SIZES = [
    { size: 'small', cpuCount: 1, memoryMB: 1024, label: 'Small: 1 vCPU, 1 GiB' },
    /*
     * `small`'s core with twice the memory, as `mediumplus` is `medium`'s — and the ChatGPT default
     * (`DEFAULT_SANDBOX_SIZE` says why). Listed before `medium` because it costs less: a size's place
     * here is its price, and these two are the case where price and memory disagree.
     */
    { size: 'smallplus', cpuCount: 1, memoryMB: 2048, label: 'Small+: 1 vCPU, 2 GiB' },
    { size: 'medium', cpuCount: 2, memoryMB: 1024, label: 'Medium: 2 vCPU, 1 GiB' },
    /*
     * The one size that is not a step up in both directions: the same two cores as `medium` with
     * twice the memory.
     *
     * It exists because the two limits are hit for different reasons. A playground runs a Vite dev
     * server, a coding agent and — for a screenshot — a headless Chrome rendering WebGL through
     * SwiftShader; the memory is what a build and a browser take at once, and the cores are what
     * that browser then pins. A game that needs room to build does not necessarily need four cores
     * to be given it, and `large` charges for both.
     */
    { size: 'mediumplus', cpuCount: 2, memoryMB: 2048, label: 'Medium+: 2 vCPU, 2 GiB' },
    /*
     * Two cores with the 4 GiB Claude Code asks for — the Claude Code default
     * (`DEFAULT_CLAUDECODE_SANDBOX_SIZE` says why).
     */
    { size: 'mediummax', cpuCount: 2, memoryMB: 4096, label: 'Medium max: 2 vCPU, 4 GiB' },
    { size: 'large', cpuCount: 4, memoryMB: 4096, label: 'Large: 4 vCPU, 4 GiB' },
    { size: 'extralarge', cpuCount: 8, memoryMB: 8192, label: 'Extra large: 8 vCPU, 8 GiB' },
];
/**
 * The sizes the Claude Code image is published at, smallest first (`docs/playground-claudecode-plan.md`
 * decision 7): the ones with the 4 GiB Anthropic asks Claude Code to have. The ChatGPT image may be
 * published at any size; this family is not.
 *
 * A team's Claude Code sandboxes have a size of their own, `teams.claudecode_sandbox_size`
 * (migration 0047), set in the admin console beside `sandbox_size` and independent of it: an
 * extra-large Claude Code machine no longer means extra-large ChatGPT ones.
 *
 * The same list as `FAMILIES.claudecode.sizes` in `playground/scripts/sizes.mjs`, which builds the
 * images; `playground/agentd/test/sizes.test.ts` holds the two together. Migration 0047's CHECK is
 * the third copy (0093's, since that migration widened it).
 */
export const CLAUDECODE_SANDBOX_SIZES = ['mediummax', 'large', 'extralarge'];
export const ClaudeCodeSandboxSize = z.enum(CLAUDECODE_SANDBOX_SIZES);
/**
 * What a team's Claude Code sandboxes are with nothing set, and what every team was moved to
 * (owner, 2026-10-27, migration 0093): the smallest the image is published at.
 *
 * It was `large`, 4 vCPU and 4 GiB. On production, Claude Code peaked at about 2 GiB at the 90th
 * percentile and had more than two cores busy for under a tenth of a session, so the 4 GiB stays
 * and two of the cores go, for about 62% of the price.
 */
export const DEFAULT_CLAUDECODE_SANDBOX_SIZE = 'mediummax';
/**
 * How long one of a team's sandboxes may last, in minutes — the ceiling (`teams.sandbox_minutes`,
 * migration 0047), counted from the sandbox's start and set in the admin console.
 *
 * The default is the sixty minutes every sandbox had when this was one number for the deployment.
 * The floor is fifteen because the handover arms ten minutes before the ceiling
 * (`ARM_MS` in `playground/agentd/src/handoff.ts`), and the top is a day, E2B's own limit on its
 * Pro plan — on Hobby, E2B ends a sandbox at sixty whatever this says. The migration's CHECK
 * mirrors the bounds.
 */
export const DEFAULT_SANDBOX_MINUTES = 60;
export const MIN_SANDBOX_MINUTES = 15;
export const MAX_SANDBOX_MINUTES = 1440;
export const SandboxMinutes = z.number().int().min(MIN_SANDBOX_MINUTES).max(MAX_SANDBOX_MINUTES);
export function sandboxSizeSpec(size) {
    // Non-null by construction: the list above covers the enum, and `test/sandbox-size.test.ts`
    // holds that up so a fifth size added to one and not the other cannot ship.
    return SANDBOX_SIZES.find((spec) => spec.size === size);
}
export const CreatePlaygroundRequest = z.object({
    template: PlaygroundTemplate.default('starter'),
    /**
     * Defaults to `canvas` **on the wire**, which is not the same as being the recommended one:
     * a caller that does not mention an engine — an older dashboard, the CLI, a test — gets the
     * playground it has always got. The dashboard picks `pixi` by sending it explicitly.
     */
    engine: PlaygroundEngine.default('canvas'),
});
/**
 * Rewrite this canvas game in PixiJS — by making a new one (§E5).
 *
 * Only the two facts the server cannot invent. The engine is not a field: v1 clones canvas to
 * pixi and nothing else, and a direction that can only have one value is a value, not a choice.
 * The package id goes through the same prefix check every other app creation goes through — it
 * is the §4 publishing boundary, and a clone is not a way around it.
 */
export const ClonePlaygroundRequest = z.object({
    name: DisplayName,
    packageName: PackageName,
});
/**
 * How much of the game's asset library has made it to the copy.
 *
 * It is reported rather than assumed because it cannot be done in one request. A Cloudflare
 * Worker gets 50 subrequests, an asset costs two of them (read the object, write the copy), and
 * a library may hold 500 — so the copy is made in batches and the dashboard calls back until
 * `remaining` is zero. Truncating quietly was the one option ruled out: a partial library is a
 * nuisance, a silent one is a bug report.
 */
export const PlaygroundAssetCopy = z.object({
    total: z.number().int(),
    copied: z.number().int(),
    remaining: z.number().int(),
});
/**
 * Why a sandbox stopped (`docs/admin-plan.md` §4.2, migration 0027).
 *
 * A **closed list**, and the value of recording any of this at all is in it being exhaustive: the
 * question the admin console exists to answer is "who used a sandbox, for how long, and why did it
 * end", and a reason of `null` on a row that ended is a path somebody forgot to account for.
 *
 * Every member is a code path in `apps/api/src/routes/playgrounds.ts` today:
 *
 *  - `stopped`     — somebody pressed Stop. The actor is in `end_detail`, because it need not be
 *                    whoever started it.
 *  - `page_closed` — the same route reached by the closing tab's beacon (`?waitForSave=1`).
 *                    "They left" and "they finished" are different facts about one button.
 *  - `reset`       — stopped, and the project thrown away.
 *  - `expired`     — past its own deadline and E2B confirms it is gone: the ordinary end of an
 *                    untouched session, reaped on the sandbox timeout and noticed by the cron.
 *  - `reclaimed`   — a teammate's start found the seat held by a sandbox E2B no longer has.
 *                    Killed from E2B's own dashboard, or crashed.
 *  - `agentd_gone` — the VM answered and agentd did not, past the boot grace. The wedge that has
 *                    no name in E2B's vocabulary.
 *  - `stale_row`   — a start died mid-flight and left a row claiming a seat. Not a session anybody
 *                    used, and it is deliberately still recorded: a seat was held.
 *  - `admin_kill`  — an admin ended it, and `end_detail` is the note they had to type. The one
 *                    reason whose *why* is recoverable from nothing else.
 *  - `idle`        — a linked playground's sandbox found nobody there for ten minutes and released
 *                    itself (`POST /v1/playgrounds/:appId/release`). One of the three endings
 *                    after which the link starts a new sandbox on its own
 *                    (`docs/playground-local-plan.md` §6).
 *  - `agent_changed` — the playground was switched to or from Claude Code while this sandbox ran,
 *                    and a start found it still running on the other image and replaced it
 *                    (`docs/playground-claudecode-plan.md` §8). Not one the link restarts after:
 *                    somebody chose it.
 *
 * The column has no CHECK, on purpose (see the migration): this is written by nine code paths on
 * the way to releasing a machine, and failing to record why a sandbox stopped must never be a
 * reason to leave it running.
 */
export const SandboxEndReason = z.enum([
    'stopped',
    'page_closed',
    'reset',
    'expired',
    'reclaimed',
    'agentd_gone',
    'stale_row',
    'admin_kill',
    'idle',
    'agent_changed',
]);
/**
 * Who does the work in a playground, when it is not the sandbox's own assistant
 * (`docs/playground-local-plan.md` §0): `external` is the developer's own agent on their laptop,
 * through `boardflight playground link`; `claudecode` is Claude Code in a terminal in a sandbox
 * of its own (`docs/playground-claudecode-plan.md`).
 *
 * The sandbox's assistant is the *absence* of the field, never a value of it, and each value
 * appears only for a team that has its feature — `playground.external`, `playground.claudecode`.
 * A field that read `codex` for everyone else would tell a team without either that something
 * else exists.
 */
export const PlaygroundAgent = z.enum(['external', 'claudecode']);
/**
 * `POST /v1/apps/:appId/playground/agent`. `codex` switches back, and is only ever asked for; each
 * of the other two is behind its own feature key.
 */
export const SetPlaygroundAgentRequest = z.object({ agent: z.enum(['codex', 'external', 'claudecode']) });
/**
 * `POST /v1/playgrounds/:appId/release` — a sandbox ending itself. Only `idle` for now: a linked
 * sandbox that found nobody there (`docs/playground-local-plan.md` §3).
 */
export const PlaygroundReleaseRequest = z.object({ reason: z.literal('idle') });
/** Which channel the person chose to publish the playground's next build to. */
export const PublishGrantRequest = z.object({ channel: z.string().trim().min(1).max(64) });
/*
 * Device keys (`docs/playground-local-plan.md` §6). A linked playground's session secret travels
 * between one person's browser and laptop encrypted to each of them; these are the routes that
 * register a device, approve one and hand it its wrap. The encoding is `./secret-wrap`, which is
 * also what checks a key and a wrap — the schemas here only shape the wire.
 */
/** A browser tab's key, or a laptop's `boardflight playground link`. */
export const PlaygroundDeviceKind = z.enum(['browser', 'link']);
/** How long a device's label may be. It is shown in one line of a prompt and one of a terminal. */
export const PLAYGROUND_DEVICE_LABEL_MAX = 80;
/** A P-256 public key as a JWK with exactly these members; `checkDevicePublicJwk` is the real check. */
export const PlaygroundDevicePublicKey = z.object({
    kty: z.literal('EC'),
    crv: z.literal('P-256'),
    x: z.string(),
    y: z.string(),
});
/**
 * `POST /v1/apps/:appId/playground/devices`. The label is shown only to the device's owner, in the
 * dashboard and in their terminal, and a control character in it would be one more way to write to
 * a terminal than printing a name should be.
 */
export const RegisterPlaygroundDeviceRequest = z.object({
    kind: PlaygroundDeviceKind,
    label: z
        .string()
        .trim()
        .min(1)
        .max(PLAYGROUND_DEVICE_LABEL_MAX)
        .regex(/^[^\p{Cc}]+$/u, 'A device label is one line of text.'),
    publicKey: z.unknown(),
});
/**
 * The answer to a registration. `approved` is false for a new key: only a device that holds the
 * current secret can approve another. Registering the same key again is how a device says it is
 * still in use, and answers with where it stands.
 */
export const PlaygroundDeviceRegistration = z.object({
    deviceId: Ulid,
    fingerprint: z.string(),
    approved: z.boolean(),
    /**
     * The playground's page in the dashboard, which is where a browser holding the secret allows
     * this device. The link opens it while it waits. Only the Worker knows the dashboard's origin,
     * the way it does for `login`'s `verificationUri`; optional, so a link talking to a Worker from
     * before it still waits, just without the address.
     */
    playgroundUrl: z.string().optional(),
});
/**
 * One of the caller's own devices on a playground — nobody is ever shown another person's. The
 * public key is here because a device holding the secret approves a waiting one by wrapping the
 * secret to it.
 */
export const PlaygroundDevice = z.object({
    id: Ulid,
    kind: PlaygroundDeviceKind,
    label: z.string(),
    fingerprint: z.string(),
    publicKey: PlaygroundDevicePublicKey,
    approved: z.boolean(),
    approvedAt: z.number().int().nullable(),
    lastSeenAt: z.number().int(),
    createdAt: z.number().int(),
});
/** A secret wrap on the wire, `{ v: 1, epk, iv, ct }`; `parseSecretWrap` is the real check. */
export const PlaygroundSecretWrap = z.object({
    v: z.literal(1),
    epk: PlaygroundDevicePublicKey,
    iv: z.string(),
    ct: z.string(),
});
/**
 * `GET /v1/apps/:appId/playground/secret?device=` — the running sandbox, and its secret wrapped to
 * this device. Opened with `unwrapSecret({ wrap, sessionId, deviceId, privateKey })`. A device with
 * nothing waiting for it is answered 404 `no_wrap`.
 */
export const PlaygroundSecret = z.object({
    sessionId: Ulid,
    agentUrl: z.string(),
    previewUrl: z.string(),
    expiresAt: z.number().int(),
    wrap: PlaygroundSecretWrap,
});
/**
 * `POST /v1/playgrounds/:appId/devices/:deviceId/approve`, authenticated with the session secret:
 * the secret, wrapped by the device that holds it to the device being approved.
 */
export const ApprovePlaygroundDeviceRequest = z.object({ wrap: z.unknown() });
export const Playground = z.object({
    id: Ulid,
    appId: Ulid,
    teamId: Ulid,
    template: PlaygroundTemplate,
    engine: PlaygroundEngine,
    status: PlaygroundStatus,
    /** From `PLAYGROUNDS.head()`, not a column — the bucket is the authority on its own object. */
    snapshotAt: z.number().int().nullable(),
    /**
     * The app this playground was cloned from, for the rewrite path. `null` for every playground
     * made the ordinary way, and `null` again if that app has since been deleted — the rewrite
     * outlives the original, but the copy can no longer be finished from it.
     */
    clonedFrom: Ulid.nullable(),
    createdAt: z.number().int(),
    /**
     * `external` for a linked playground, `claudecode` for a Claude Code one — each only for a team
     * with its feature; absent otherwise. What the playground is set to, which its next fresh start
     * follows; a sandbox already running says what *it* is in `PlaygroundSession.agent`.
     */
    agent: PlaygroundAgent.optional(),
    /**
     * How the last sandbox ended, and only alongside `agent`. The link restarts a sandbox after an
     * ending nobody chose and after no other (`docs/playground-local-plan.md` §6). `null` until one
     * has ended.
     */
    lastEnd: z.object({ reason: SandboxEndReason, at: z.number().int() }).nullable().optional(),
    /**
     * Who started the running sandbox, and so whose devices hold it — *Linked by @honza* on a
     * teammate's screen (`docs/playground-local-plan.md` §9). Only alongside `agent`, `null` while
     * nothing runs, and resolved like every other person in a response (`identitiesFor`).
     */
    linkedBy: UserIdentity.nullable().optional(),
});
/**
 * Why a version of a project was kept (docs/playground-history-plan.md §1).
 *
 * The kind decides two things and nothing else: what the dashboard calls it, and whether the
 * rolling window prunes it.
 *
 *  - `turn`    — taken at the top of an AI turn, before the assistant writes anything. Labelled
 *                with the prompt that is about to run, so restoring one means *the project as it
 *                was before I asked for that*. The rolling window.
 *  - `deploy`  — taken when a deploy finalizes, labelled with the build's version name. Never
 *                pruned: a state that was shipped stays reachable however long ago it shipped.
 *  - `restore` — the current project, captured immediately before a rollback overwrites it. It
 *                exists so that pressing Restore is itself undoable, and it prunes with `turn`.
 */
export const PlaygroundVersionKind = z.enum(['turn', 'deploy', 'restore']);
/**
 * How many prunable versions a project keeps.
 *
 * Twenty covers an afternoon of prompting — the window a rollback is ever reached for is the last
 * few turns, and a list longer than a screen is a list nobody reads. `deploy` versions are outside
 * this count entirely; a game that has shipped two hundred times keeps two hundred of them.
 *
 * The number is enforced in one place, `pruneVersions` in `apps/api/src/routes/playgrounds.ts`,
 * which runs inside the completion of a new version — the only moment the count can grow.
 */
export const PLAYGROUND_VERSION_WINDOW = 20;
/**
 * How long an uncompleted version row is allowed to sit before the prune deletes it.
 *
 * A row is written before its bytes exist, because the row's id is what the object's key is made
 * of. One that never completed is an upload that died: not a version, and not something to leave
 * in a table forever either. An hour is far longer than a pack-and-upload takes and far shorter
 * than anybody would notice.
 */
export const PLAYGROUND_VERSION_PENDING_MS = 60 * 60 * 1000;
/**
 * One kept state of a playground project (docs/playground-history-plan.md).
 *
 * A version is the whole project at a moment — the same gzipped tar the snapshot is, packed once
 * and uploaded twice — and it is never anything smaller. There is no per-file restore and no diff
 * on this shape, deliberately: the Worker brokers these objects and never opens one, so nothing
 * outside a sandbox knows what is in it.
 */
export const PlaygroundVersion = z.object({
    id: Ulid,
    kind: PlaygroundVersionKind,
    /** The prompt, the version name, or what was restored. Never empty; trimmed to 120 chars. */
    label: z.string(),
    /** Bytes of the archive. Only a completed version is ever serialized, so never null here. */
    size: z.number().int(),
    createdAt: z.number().int(),
});
/** How much of a prompt is kept as a label. Long enough to recognise, short enough for a row. */
export const PLAYGROUND_VERSION_LABEL_MAX = 120;
/**
 * What agentd asks for when it wants somewhere to put a version.
 *
 * The archive is already packed and hashed when this is sent, which is what lets the answer be
 * "don't bother": `sha256` is compared against the newest kept version, and a `turn` whose project
 * is byte-identical to it is answered `{ unchanged: true }` with no row and no upload. Four
 * questions in a row therefore cost four hashes rather than four copies of the same project.
 *
 * `sha256` is not a security property — the sandbox wrote the object it is describing. It is how
 * that comparison happens without the Worker opening either archive, which it is not allowed to do.
 *
 * The label is normalized server-side rather than trusted: it is whatever somebody typed into a
 * chat box, and it is rendered in a list.
 */
export const CreatePlaygroundVersionRequest = z.object({
    kind: PlaygroundVersionKind,
    label: z.string().max(4000).default(''),
    size: z.number().int().positive(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    /**
     * A deploy version's build: the build this tree was published as (`docs/game-events-plan.md`
     * §6.4). What lets a later promotion rebuild exactly the tree that shipped. Deploys only.
     */
    buildId: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/).optional(),
});
/**
 * Somewhere to put a version, or the news that there is nothing worth putting.
 *
 * Exactly one of the two shapes: `unchanged` means no row was written and nothing should be
 * uploaded. agentd treats it as success, because it is.
 */
export const PlaygroundVersionTarget = z.union([
    z.object({ unchanged: z.literal(true) }),
    z.object({ versionId: Ulid, putUrl: z.string() }),
]);
export const PlaygroundSession = z.object({
    playground: Playground,
    previewUrl: z.string(), // vite dev server
    agentUrl: z.string(), // agentd
    /**
     * Appears exactly once per sandbox start; the server stores only its hash. Empty string on
     * reconnect — the browser kept its copy or must stop/start.
     */
    sessionToken: z.string(),
    /**
     * `null` in the shape this ships in: sandbox URLs are public and agentd's session secret is
     * the gate (see the P1.0 spike). Kept because it is what E2B hands back when public traffic
     * is disallowed, and that door should reopen without a schema change.
     */
    trafficAccessToken: z.string().nullable(),
    /** The sandbox's wall-clock deadline, for the UI countdown. */
    expiresAt: z.number().int(),
    /**
     * Which sandbox this is: the `playground_sessions` row its start opened. A stop that names it
     * (`?session=`) is refused once another sandbox has replaced this one, and a secret wrap is bound
     * to it. Absent from a Worker older than the linked playground (`docs/playground-local-plan.md`
     * §8).
     */
    sessionId: Ulid.optional(),
    /**
     * What this sandbox is: `external` for a linked one, `claudecode` for one on the Claude Code image
     * with Claude Code in its terminal, absent for the assistant's. For a start, what `Playground.agent`
     * says; for a reconnect, what the sandbox was started as — a Claude Code sandbox stays one until
     * it ends, whatever has happened to the key since.
     */
    agent: PlaygroundAgent.optional(),
});
/**
 * What `POST /v1/playgrounds/:appId/lease` answers: agentd's one conversation with the Worker,
 * authenticated with the session secret. `granted: false` means the ceiling is spent — a real
 * answer, not a failure, and the one case where the right response is to stop asking.
 */
export const PlaygroundLease = z.object({
    granted: z.boolean(),
    leaseSeconds: z.number(),
    expiresAt: z.number().int(),
    /**
     * When this sandbox's ceiling falls, as an absolute moment.
     *
     * Not a lease and not derived from one: the ceiling is fixed when the sandbox starts, and moves
     * only when its clock is refreshed (`docs/sandbox-handover-plan.md` §5) — later, never earlier.
     * It is here because the handover needs it (§3) and this is the only conversation agentd has
     * with the side that knows it.
     *
     * Absent from a Worker older than the handover, and from a sandbox with no broker at all. Both
     * mean the same thing to everything downstream: no ceiling is known, so nothing hands over.
     */
    ceilingAt: z.number().int().optional(),
    /**
     * Present, and true, only when the row says `external` and the team has `playground.external`.
     * It is how agentd's live flip to linked mode asks permission, because agentd has no notion of
     * features (`docs/playground-local-plan.md` §3). Absent is a refusal, including from a Worker
     * older than the field.
     */
    linked: z.literal(true).optional(),
});
/**
 * The answer to `POST /v1/apps/:appId/playground/refresh` — the sandbox's clock, started again
 * (`docs/sandbox-handover-plan.md` §5).
 *
 * `refreshed: false` is not a failure. It is a refresh somebody else — another tab — made a
 * moment ago, and `expiresAt` is the deadline that one set: the answer every caller wanted.
 */
export const PlaygroundClockRefresh = z.object({
    refreshed: z.boolean(),
    /** The new ceiling, as an absolute moment. What `StoredSession.expiresAt` becomes. */
    expiresAt: z.number().int(),
    sessionId: z.string().optional(),
});
/**
 * The answer to agentd's `POST /v1/playgrounds/:appId/targets`: the URLs it saves through, minted
 * for the run its clock is on now. Keyed by the session file's own `BF_*` names, so agentd reads
 * them with the parser it boots with; `expiresAt` is when they stop working.
 */
export const PlaygroundTargets = z.object({
    urls: z.record(z.string(), z.string()),
    expiresAt: z.number().int(),
    /** The ceiling these were minted for — what agentd compares the next moved one against. */
    ceilingAt: z.number().int(),
});
/**
 * The `refresh` block of agentd's *authenticated* `GET /status` (`docs/sandbox-handover-plan.md`
 * §5): present on an image that can have its clock refreshed, absent from every one before it —
 * which is how the dashboard knows not to offer it. A sandbox from before this was handed URLs
 * that expire with its first ceiling and cannot be given more, so refreshing its clock would buy
 * an hour in which nothing saves.
 *
 * `busy` is why not now, in words for a tooltip — the assistant is working, Claude Code is, a
 * deploy is running — or `null` when a refresh would interrupt nothing.
 */
export const PlaygroundRefreshStatus = z.object({
    version: z.literal(1),
    busy: z.string().nullable(),
});
/**
 * The `link` block of agentd's *authenticated* `GET /status` in a linked sandbox
 * (`docs/playground-local-plan.md` §3). `version` is what the dashboard and the CLI check before
 * relying on anything else linked mode adds; a sandbox without the block predates it.
 */
export const PlaygroundLinkStatus = z.object({
    version: z.literal(1),
    /** `external`, or absent. Only ever the link's: a Claude Code sandbox has no link block. */
    agent: z.literal('external').optional(),
    /** When the sandbox's one active link last made a request; `null` when none has claimed it. */
    seenAt: z.number().int().nullable(),
    /** What that link last reported as changed on the laptop and not yet applied. */
    pending: z.number().int().nonnegative().optional(),
    /** The reinstall an apply started when `package.json` changed, and how the last one went. */
    install: z.object({ running: z.boolean(), error: z.string().nullable() }).optional(),
    /**
     * The newest entry of the sandbox's sync activity (`GET /link/activity`), so a page polling this
     * asks for the list only when there is something new on it. Absent from an image from before it.
     */
    activity: z.number().int().optional(),
});
/**
 * The `terminal` block of agentd's *authenticated* `GET /status`, present only in a Claude Code
 * sandbox (`docs/playground-claudecode-plan.md` §5). `version` is `PLAYGROUND_TERM_PROTOCOL`'s
 * major, what the page checks before it opens `WS /term`; `running` is whether the shell is up.
 * The ChatGPT image's answer has no such block, so an image from before the split and a ChatGPT
 * sandbox read the same.
 *
 * The two flags say what an image newer than the first Claude Code one can do, so the page offers a
 * control only to a sandbox that will answer it (`docs/playground-claudecode-mod.md` §6): `turns`,
 * that Claude Code's hooks tell agentd when a turn starts and ends, which the `claude` event on the
 * chat socket then carries; `attachments`, that `POST /attachments` answers the file's `path`, for
 * the page to paste into the terminal as an `@` mention; `selection`, that agentd holds the
 * inspector's selection for Claude Code's prompt hook (`PUT /claude/selection`), so the page shows
 * it on the terminal instead of drafting it into a message. Absent from an image from before them.
 */
export const PlaygroundTerminalStatus = z.object({
    version: z.literal(1),
    running: z.boolean(),
    turns: z.literal(true).optional(),
    attachments: z.literal(true).optional(),
    selection: z.literal(true).optional(),
});
/**
 * One entry of a linked sandbox's change feed (`GET /changes`, agentd's `changes.ts`): a path, what
 * happened to it, and — for an upsert — the hash of what is there now. The link fetches bytes
 * separately, and only for what it does not already have. `origin` is a courtesy: the link tells
 * its own writes apart by hash, never by this.
 */
export const PlaygroundChange = z.object({
    seq: z.number().int(),
    path: z.string(),
    kind: z.enum(['upsert', 'delete']),
    sha256: z.string().optional(),
    size: z.number().int().optional(),
    origin: z.enum(['sandbox', 'apply', 'merge']),
    at: z.number().int(),
});
/**
 * How long a sandbox-usage row is kept (`playground_sessions`, migration 0027).
 *
 * 180 days, the same window a feedback screenshot gets, and for the same kind of reason rather
 * than a coincidence of numbers. Every other row-per-event table in this system has a rule —
 * install events, raw analytics, screenshots — and this one names a person: `started_by` says who
 * was working, on what, at what hour of which evening. A record kept forever because nobody wrote
 * down when it should go is the thing those rules exist to prevent.
 *
 * What it costs is history: "how much has this team used" can only be asked back this far. That is
 * the right trade at the scale this console operates on — the number a seat grant is argued from
 * is the last thirty days, not the last three years — and a longer window would be a decision to
 * take deliberately rather than by default.
 *
 * Only **ended** rows are swept. An open one is a sandbox somebody may still be in, and its age is
 * a symptom rather than a reason to forget it.
 */
export const SANDBOX_SESSION_RETENTION_DAYS = 180;
//# sourceMappingURL=playgrounds.js.map